import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../utils/cn'

/**
 * Single source of truth for the accent fill used by selected pills +
 * primary CTAs.
 *
 * Flat brand red — no gradient, specular highlight, or shadow (the
 * previous "glass" treatment was dropped per design feedback). The
 * `--color-accent` token means a rebrand still touches one place in
 * `packages/shared/styles/tokens.css` and every accent surface follows.
 * Exported as a `style`-prop constant so callers that already have
 * their own element (e.g. SidebarNavItem's NavLink) can apply just the
 * fill without wrapping; the AccentGlassPill component consumes it for
 * the standalone-pill case.
 */
export const ACCENT_GLASS_FILL_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-accent)',
}

interface Props {
  readonly children: ReactNode
  readonly className?: string
}

/**
 * Glass-tinted accent pill — small standalone label like the Bay-N
 * badge in the dashboard calendar. Defaults match the badge's
 * mockup-derived dimensions; pass `className` to override (e.g.
 * different rounding, padding) while keeping the gradient + shadow
 * recipe.
 */
export function AccentGlassPill({ children, className }: Props) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-control text-white text-[11px] font-extrabold tracking-wider font-display whitespace-nowrap backdrop-saturate-glass',
        className,
      )}
      style={ACCENT_GLASS_FILL_STYLE}
    >
      {children}
    </span>
  )
}
